import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
  Font,
} from "@react-pdf/renderer";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import map from "lodash/fp/map";
import negate from "lodash/fp/negate";
import includes from "lodash/fp/includes";

import { userChoiceValuesForPDF } from "./PDFTransformUserChoiceValues";

const styles = StyleSheet.create({
  page: { fontFamily: "dinCondensed", letterSpacing: 1.1, lineHeight: 1.3 },
  header: {
    height: "70px",
    padding: "23px",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  headerLogo: {
    width: "100%",
  },
  recipeContainer: {
    display: "flex",
    flexDirection: "row",
    textTransform: "uppercase",
    alignItems: "center",
    lineHeight: 1,
  },
  recipeID: {
    fontFamily: "dinCondensedBold",
  },
  productImageContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginTop: "35px",
  },
  productImage: {
    zIndex: 20000,
    width: "420px",
    objectFit: "contain",
    backgroundColor: "white",
  },
  reviewOverviewSpecs: {
    marginTop: "25px",
    marginLeft: "100px",
    marginRight: "100px",
    marginBottom: "100px",
    display: "flex",
    flexDirection: "row",
  },
  SpecColumn: {
    width: "50%",
    marginRight: "50px",
  },

  specCustomisation: {
    borderBottom: "solid",
    borderBottomWidth: "1px",
    borderColor: "#E6E6E6",
    marginBottom: "10px",
  },

  specCustomisationName: {
    fontFamily: "dinCondensedBold",
    color: "#009CDA",
    textTransform: "capitalize",
    fontSize: "12px",
    marginBottom: "5px",
  },

  specsOptionsValues: {
    display: "flex",
  },

  horizontalDivider: {
    border: "solid",
    borderWidth: "1px",
    borderColor: "#E6E6E6",
  },

  specOptionLabel: {
    fontSize: "16px",
    marginBottom: "5px",
    textTransform: "capitalize",
  },
  specsOptionSingleValue: {
    display: "inline",
    flexDirection: "row",
    textTransform: "capitalize",

    fontSize: "16px",
  },
  specsOptionMultiValue: {
    paddingBottom: "5px",
    textTransform: "capitalize",
    fontWeight: "bold",
    fontSize: "14px",
  },
  specsSubOptions: {
    display: "flex",
    flexDirection: "row",
    marginLeft: "20px",
    marginBottom: "5px",
  },
  specsValue: {
    color: "black",
    fontFamily: "dinCondensedBold",
  },
  infoBoxContainer: {
    width: "300px",
    position: "absolute",
    right: "120px",
    bottom: "120px",
    lineHeight: 1.3,
    letterSpacing: 1.1,
  },
  infoBox: {
    backgroundColor: "#E6E6E6",
    fontSize: "12px",
    padding: "20px",
  },
});

const infoBoxText =
  "Take your MyBauer Goalie Custom printout to your local retailer to place your order. Use the “Find a Dealer” feature on Bauer.com to find approved Custom Goal Dealers. All Bauer custom sales are final and fall under our regular warranty policy.";

const PDFContent = ({
  userChoicesForUiV2,
  appHeaderLogo,
  userChoiceValues,
  productLogo,
  headerBackgroundColour,
  recipeID,
  images,
  publicDirectoryUrl,
}) => {
  // Register Bauer fonts

  Font.register({
    family: "dinCondensed",
    fonts: [
      {
        src: publicDirectoryUrl + "/fonts/pdfFonts/dinCondensed.ttf",
        fontStyle: "normal",
        fontWeight: 400,
      },
    ],
  });

  Font.register({
    family: "dinCondensedBold",
    fonts: [
      {
        src: publicDirectoryUrl + "/fonts/pdfFonts/dinCondensedBold.ttf",
      },
    ],
  });

  const pdfUserChoiceValues = userChoiceValuesForPDF({
    userChoicesForUiV2,
    userChoiceValues,
  });

  const pdfUserChoiceValuesWithImages = flow(
    map((productUserChoices) => {
      const productImage = filter(({ imageSet }) =>
        productUserChoices.productSet.includes(imageSet)
      )(images);

      return { ...productUserChoices, ...productImage[0] };
    })
  )(pdfUserChoiceValues);

  const changeImageURL = (url) => {
    if (url.match(/\.(jpeg|jpg|gif|webp|png)$/) != null) {
      return url.replace(/\.(jpeg|jpg|gif|webp)$/, ".png");
    }
    return url.concat(".png");
  };

  return (
    <Document>
      {pdfUserChoiceValuesWithImages.map(
        ({ productSet, productUserChoiceValues, finalImage }) => {
          const column1 = flow(
            filter(({ name }) => negate(includes("specs"))(name)),
            filter(({ name }) => negate(includes("personalization"))(name))
          )(productUserChoiceValues);

          const column2 = flow(
            filter(
              ({ name }) =>
                includes("specs")(name) || includes("personalization")(name)
            )
          )(productUserChoiceValues);

          const productColumns = [column1, column2];

          return (
            <Page size="A3" style={styles.page} key={productSet}>
              {/* HEADER */}
              <View
                style={{
                  ...styles.header,
                  backgroundColor: headerBackgroundColour,
                }}
              >
                <View style={{ display: "flex", flexDirection: "row" }}>
                  <Image
                    src={changeImageURL(appHeaderLogo.url)}
                    style={{
                      zIndex: 20000,
                      width: "150px",
                      objectFit: "contain",
                      marginRight: "10px",
                    }}
                  />
                  <Svg
                    style={{
                      zIndex: 20000,
                      height: "24px",
                      width: "12px",
                      marginRight: "10px",
                    }}
                  >
                    <Path d="M11 0H12L1 24H0L11 0Z" fill="#4D4D4D" />
                  </Svg>
                  <Image
                    src={changeImageURL(productLogo.url)}
                    style={{
                      zIndex: 20000,
                      width: "136px",
                      objectFit: "contain",
                    }}
                  />
                </View>

                <View style={styles.recipeContainer}>
                  <Text>RecipeID: </Text>
                  <Text style={styles.recipeID}>{recipeID}</Text>
                </View>
              </View>

              {/* PRODUCT IMAGE  */}
              <View style={styles.productImageContainer}>
                <Image src={finalImage} style={styles.productImage} />
              </View>

              {/* COLUMN SPECS */}
              <View style={styles.reviewOverviewSpecs}>
                {productColumns.map((column) => (
                  <View style={styles.SpecColumn} key={column}>
                    {column.map(({ title, name, values }) => {
                      return (
                        <View style={styles.specCustomisation} key={name}>
                          <Text style={styles.specCustomisationName}>
                            {title}
                          </Text>
                          {values.map((valueOrValues, index) => {
                            return (
                              <View
                                style={styles.specsOptionsValues}
                                key={index}
                              >
                                <View
                                  style={
                                    !Array.isArray(valueOrValues.value)
                                      ? styles.specsOptionSingleValue
                                      : styles.specsOptionMultiValue
                                  }
                                >
                                  <Text style={styles.specOptionLabel}>
                                    {valueOrValues.label &&
                                      `${valueOrValues.label}: `}
                                  </Text>

                                  {!Array.isArray(valueOrValues.value) ? (
                                    <Text style={styles.specsValue}>
                                      {valueOrValues.value}
                                    </Text>
                                  ) : (
                                    valueOrValues.value.map(
                                      ({ label, value }) => {
                                        return (
                                          <View
                                            style={styles.specsSubOptions}
                                            key={label}
                                          >
                                            <Text>
                                              {`${label}${value && ": "}`}
                                              {value && (
                                                <Text style={styles.specsValue}>
                                                  {value}
                                                </Text>
                                              )}
                                            </Text>
                                          </View>
                                        );
                                      }
                                    )
                                  )}
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>

              {/* INFO TEXT BOX*/}
              <View style={styles.infoBoxContainer}>
                <Text style={styles.infoBox}>{infoBoxText}</Text>
              </View>
            </Page>
          );
        }
      )}
    </Document>
  );
};

export default PDFContent;
